import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { StateContext } from "../../../../store";
import { isAuthorized } from "../../../../routes/IsAuthorized";
import Badge from "@mui/material/Badge";
import { notificationsApi } from "../../../../services/NotificationServices";

const SideNavBar = (props) => {
  const { pages, onClose, ...rest } = props;
  const navigate = useNavigate();
  const { role } = useContext(StateContext);
  const location = useLocation();
  const pathname = location.pathname;
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [notificationCount, setNotificationCount] = useState(0);

  const getCurrentUserId = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id || null;
  };

  useEffect(() => {
    const fetchNotificationCount = () => {
      const userId = getCurrentUserId();
      if (!userId) return;

      notificationsApi("get", userId, null, {
        page: 1,
        per_page: 1,
      }).then((res) => {
        setNotificationCount(res?.meta?.count || 0);
      }).catch((error) => {
        console.error('Error fetching notification count:', error);
      });
    };

    fetchNotificationCount();
    
    if (pathname === '/management/notifications') {
      fetchNotificationCount();
    }

    const handleNotificationUpdate = () => {
      fetchNotificationCount();
    };
    window.addEventListener('notificationCountUpdate', handleNotificationUpdate);

    return () => {
      window.removeEventListener('notificationCountUpdate', handleNotificationUpdate);
    };
  }, [pathname]);

  const navigationDetail = (route, index) => {
    navigate(route, { state: { index: index } });
    if (onClose) onClose();
  };

  const toggleDropdown = (pageIndex) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [pageIndex]: !prev[pageIndex]
    }));
  };

  return (
    <nav className="flex flex-col gap-1 px-2" {...rest}>
      {pages
        ?.filter((val) => val?.visible !== false)
        ?.filter((val) => isAuthorized(role, val?.authorized))
        ?.map((page, index) =>
          page?.children?.length > 0 ? (
            <div key={index} className="rounded-lg overflow-hidden ">
              <button
                onClick={() => toggleDropdown(page.index)}
                className="w-full text-left px-4 py-3 text-[14px] font-medium text-[#4A5568] hover:bg-white hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-lg transition-all flex items-center justify-between"
              >
                <span className="flex items-center gap-3">
                  {page?.icon}
                  {page?.title}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${openDropdowns[page.index] ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdowns[page.index] && (
                <div className="bg-[#F8FAFC] ml-2 pl-2 border-l border-gray-200 my-1 flex flex-col gap-1 rounded-r-lg">
                  {page?.children
                    ?.filter((child) => child?.visible !== false)
                    ?.map((child, childIndex) =>
                      child?.sub_children?.length > 0 ? (
                        <div key={childIndex}>
                          <button
                            onClick={() => toggleDropdown(`${page.index}-${child.index}`)}
                            className="w-full text-left px-6 py-2 text-[13px] text-[#4A5568] hover:bg-white rounded-md transition-colors flex items-center justify-between"
                          >
                            {child?.title}
                            <svg
                              className={`w-3 h-3 text-gray-400 transition-transform ${openDropdowns[`${page.index}-${child.index}`] ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {openDropdowns[`${page.index}-${child.index}`] && (
                            <div className="bg-white ml-2 pl-2 border-l border-gray-200 flex flex-col gap-0.5">
                              {child?.sub_children
                                ?.filter((sub) => sub?.visible !== false)
                                ?.map((sub, subIndex) => (
                                  <button
                                    key={subIndex}
                                    onClick={() => navigationDetail(sub?.href, sub?.index)}
                                    className={`w-full text-left px-6 py-2 text-[13px] transition-colors rounded-md ${
                                      pathname === sub?.href 
                                        ? 'text-[#5B6B8F] font-semibold bg-[#E9EFFB]' 
                                        : 'text-[#64748B] hover:bg-gray-50'
                                    }`}
                                  >
                                    {sub?.title}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          key={childIndex}
                          onClick={() => navigationDetail(child?.href, child?.index)}
                          className={`w-full text-left px-6 py-2 text-[13px] transition-colors rounded-md ${
                            pathname === child?.href
                              ? 'text-[#5B6B8F] font-semibold bg-[#E9EFFB]'
                              : 'text-[#4A5568] hover:bg-white'
                          }`}
                        >
                          {child?.title}
                        </button>
                      )
                    )}
                </div>
              )}
            </div>
          ) : (
            <button
              key={index}
              onClick={() => navigationDetail(page.href, page?.index)}
              className={`w-full text-left px-4 py-3 text-[14px] font-medium transition-all rounded-lg flex items-center gap-3 ${
                pathname === page.href
                  ? 'bg-[#5B6B8F] text-white shadow-[0_4px_12px_rgba(91,107,143,0.3)]'
                  : 'text-[#4A5568] hover:bg-white hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
              }`}
            >
              {page?.icon && (
                <span className="text-lg">
                  {page.title === "Notifications" ? (
                    <Badge 
                      badgeContent={notificationCount > 0 ? notificationCount : 0} 
                      color="error"
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '10px',
                          height: '16px',
                          minWidth: '16px',
                          padding: '0 4px',
                        },
                      }}
                    >
                      {page?.icon}
                    </Badge>
                  ) : (
                    page?.icon
                  )}
                </span>
              )}
              {page?.title}
            </button>
          )
        )}
    </nav>
  );
};

SideNavBar.propTypes = {
  className: PropTypes.string,
  pages: PropTypes.array.isRequired,
};

export default SideNavBar;